// A child's compliance in one line (02-DESIGN/03-console/01-the-console.md §
// conventions: the moves, page by page — Workspaces, a child's page):
// *version 7 applied of 7 · no drift · heard from 2 minutes ago*, with the
// findings behind it, opened in place.

import { blueprint } from "@impire-io/sdk";

import { plural, since, when } from "../../ui/components.tsx";
import type { Attested } from "./blueprint.ts";

/** complianceLine is a child's compliance in one line, and whether its findings are worth opening. */
export function complianceLine(
  written: number | undefined,
  a: Attested,
  now = Date.now(),
): { text: string; findings: boolean; tone: "ok" | "info" | "bad" | "neutral" } {
  if (a.state === "none" || a.state === "not-yet") {
    return written === undefined
      ? { text: "No blueprint, so no steward and nothing to attest.", findings: false, tone: "neutral" }
      : { text: "Not yet attested: its steward has not reported.", findings: false, tone: "neutral" };
  }
  if (a.state === "refused") {
    return {
      text: `Its last report, ${since(a.received, now) || when(a.received)}, does not verify.`,
      findings: true,
      tone: "bad",
    };
  }
  const r = a.report;
  const drift = (r.drift ?? []).length;
  const notApplied = (r.items ?? []).filter((i) => i.state !== blueprint.ItemState.applied).length;
  const parts = [
    `version ${String(r.revision_applied)} applied of ${String(written ?? r.revision_seen)}`,
    drift === 0 ? "no drift" : plural(drift, "drift finding"),
  ];
  if (notApplied > 0) {
    parts.push(`${String(notApplied)} not yet applied`);
  }
  parts.push(`heard from ${since(a.received, now) || when(a.received)}`);
  const behind = written !== undefined && r.revision_applied !== written;
  return {
    text: parts.join(" · "),
    findings: drift > 0 || notApplied > 0 || (r.workloads ?? []).length > 0 || a.silent,
    tone: a.silent || drift > 0 || notApplied > 0 ? "bad" : behind ? "info" : "ok",
  };
}
